import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import {
  Autocomplete,
  Alert,
  Avatar,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Close,
  DeleteOutline,
  AutoAwesome,
  Check,
  ContentCopy,
  Refresh,
  ErrorOutline,
  Undo,
} from "@mui/icons-material";
import { font } from "../../styles/font";
import { useEffect, useState } from "react";
import { useApplicationContext } from "../../context/ApplicationContext";
import {
  assignProjectUsers_API,
  getProjectCategoryList_API,
  getProjectDetail_API,
  getProjectList_API,
  getProjectSubcategoryList_API,
  updateProject_API,
} from "../../api/controller/ProjectController";
import { HttpStatusCode } from "axios";
import { Severity } from "../../constant/ApplicationVariables";
import useErrorHandler from "../../api/errorHandler/useErrorHandler";
import { useTranslation } from "react-i18next";
import { themePalette } from "../../styles/theme";

//  AI imports
import { AICustomIcon } from "../../components/styled/CustomIcons";
import useAiImproveText from "../hooks/useAiImproveText";

class ProjectTabs {
  static detail = 0;
  static configuration = 1;
  static users = 2;
}

export default function EditProjectDrawer({
  open,
  handleClose,
  handleContinue,
}) {
  const {
    projectList,
    setProjectList,
    handleSnackbar,
    activeProject,
    setActiveProject,
    teamsList,
    setTeamsList,
  } = useApplicationContext();
  const theme = useTheme();
  const errorHandler = useErrorHandler();
  const { t } = useTranslation("translation", { keyPrefix: "project" });

  const [isLoading, setIsLoading] = useState(false);

  const [selectedTab, setSelectedTab] = useState(0);
  const [projectData, setProjectData] = useState(activeProject);
  const [error, setError] = useState(null);
  const [activeProjectCategory, setActiveProjectCategory] = useState(null);
  const [activeProjectSubcategory, setActiveProjectSubcategory] =
    useState(null);

  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);

  // State for selected items
  const [selectedItemsList, setSelectedItemsList] = useState([]);
  const [inputValue, setInputValue] = useState("");

  // ────────────────────────────────────────────────────────────────
  //  AI improvement hook (suggestion mode) — same as Create Drawer
  // ────────────────────────────────────────────────────────────────
  const {
    isImproving,
    improvementCount,
    aiError,
    setAiError,
    pendingSuggestion,
    hasPendingSuggestion,
    originalText,
    improve,
    acceptSuggestion,
    revertSuggestion,
    reset: resetAi,
    copyToClipboard,
    totalPrompts,
  } = useAiImproveText({
    onSuggestion: (_, { promptIndex }) => {
      handleSnackbar(
        `AI suggestion ready — accept or revert (${promptIndex + 1}/${totalPrompts})`,
        Severity.info
      );
    },
    onApplied: () => {
      handleSnackbar("Floxpad AI suggestion applied", Severity.success);
    },
    onError: (msg) => handleSnackbar(msg, Severity.error),
  });

  const toggleDrawer = () => {
    handleClose();
    resetAi();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateInputs();
  };

  const validateInputs = () => {
    let isValid = true;
    const errorObject = {};

    if (!projectData?.name) {
      errorObject.name = "Name is required.";
      isValid = false;
    } else if (projectData?.name?.length < 3) {
      errorObject.name = "Name must be at least 3 characters long.";
      isValid = false;
    } else if (projectData?.name?.length > 20) {
      errorObject.name = "Name must be at most 20 characters long.";
      isValid = false;
    }

    if (!projectData?.description) {
      errorObject.description = "Description is required.";
      isValid = false;
    } else if (projectData?.description?.length < 3) {
      errorObject.description =
        "Description must be at least 3 characters long.";
      isValid = false;
    } else if (projectData?.description?.length > 500) {
      errorObject.description =
        "Description must be at most 500 characters long.";
      isValid = false;
    }

    if (!projectData?.categoryName) {
      errorObject.categoryName = "Category is required.";
      isValid = false;
    }

    if (projectData?.categoryName && !projectData?.subCategoryName) {
      errorObject.subCategoryName = "Sub Category is required.";
      isValid = false;
    }

    setError(errorObject);

    if (isValid && Object.keys(errorObject).length === 0) {
      handleUpdate();
    }
  };

  // Real-time validation function
  const validateField = (name, value) => {
    let fieldError = null;

    if (name === "name") {
      if (!value) {
        fieldError = "Name is required.";
      } else if (value.length < 3) {
        fieldError = "Name must be at least 3 characters long.";
      } else if (value.length > 20) {
        fieldError = "Name must be at most 20 characters long.";
      }
    }

    if (name === "description") {
      if (!value) {
        fieldError = "Description is required.";
      } else if (value.length < 3) {
        fieldError = "Description must be at least 3 characters long.";
      } else if (value.length > 500) {
        fieldError = "Description must be at most 500 characters long.";
      }
    }

    return fieldError;
  };

  const handleCheckButtonDisable = () => {
    let disable = false;

    if (projectData?.name !== activeProject?.name) disable = true;
    if (projectData?.description !== activeProject?.description) disable = true;
    if (projectData?.categoryName !== activeProject?.categoryName) disable = true;
    if (projectData?.subCategoryName !== activeProject?.subCategoryName)
      disable = true;
    if (projectData?.status !== activeProject?.status) disable = true;
    if (projectData?.useCasePrefix !== activeProject?.useCasePrefix)
      disable = true;
    if (projectData?.userStoryPrefix !== activeProject?.userStoryPrefix)
      disable = true;
    if (projectData?.testCasePrefix !== activeProject?.testCasePrefix)
      disable = true;

    return disable;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "categoryName" || name === "subCategoryName") {
      setProjectData({ ...projectData, [name]: e.target.value?.name });
    } else {
      setProjectData({ ...projectData, [name]: value });

      // Clear AI error when user edits description manually
      if (name === "description") {
        setAiError(null);
      }

      // Real-time validation for name and description fields
      if (name === "name" || name === "description") {
        const fieldError = validateField(name, value);

        setError((prevError) => {
          const newError = { ...prevError };

          if (fieldError) {
            newError[name] = fieldError;
          } else {
            delete newError[name];
          }

          return Object.keys(newError).length > 0 ? newError : null;
        });
      }
    }
  };

  const handleUpdate = () => {
    if (selectedTab === ProjectTabs.users) {
      handleUpdateAssignUser();
    } else {
      handleUpdateProject(projectData);
    }
  };

  // ────────────────────────────────────────────────────────────────
  //  AI Handlers
  // ────────────────────────────────────────────────────────────────
  const handleImproveText = async () => {
    await improve(projectData?.description || "");
  };

  const handleAcceptSuggestion = () => {
    acceptSuggestion((newText) =>
      setProjectData((prev) => ({ ...prev, description: newText }))
    );
  };

  const handleRevertSuggestion = () => {
    revertSuggestion();
    handleSnackbar("AI suggestion reverted", Severity.info);
  };

  const handleCopySuggestion = async () => {
    if (!pendingSuggestion?.text) return;
    const ok = await copyToClipboard(pendingSuggestion.text);
    handleSnackbar(
      ok ? "Suggestion copied!" : "Copy failed",
      ok ? Severity.success : Severity.error
    );
  };

  const handleCopyImproved = async () => {
    const ok = await copyToClipboard(projectData?.description || "");
    handleSnackbar(
      ok ? "Copied to clipboard!" : "Copy failed",
      ok ? Severity.success : Severity.error
    );
  };

  const handleResetToOriginal = () => {
    if (originalText) {
      setProjectData((prev) => ({ ...prev, description: originalText }));
      resetAi();
      handleSnackbar("Reset to original description", Severity.info);
    }
  };

  // Handle selection change
  const handleChangeAutocomplete = (event, newValue) => {
    if (newValue?.length > 0 && newValue?.length > selectedItemsList?.length) {
      setSelectedItemsList(newValue);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleDeleteItems = (data) => {
    setSelectedItemsList(
      selectedItemsList.filter((item) => item.email !== data.email)
    );
  };

  const handleGetCategoryList = async () => {
    try {
      const response = await getProjectCategoryList_API();

      if (response.status === HttpStatusCode.Ok) {
        setCategoryList(response.data);
      }

      if (response) {
        errorHandler(response);
      }
    } catch (error) {
      console.log(error, "categoryreponse errorr");
    }
  };

  const handleGetsubCategoryList = async () => {
    try {
      let query = `parentName=${projectData?.categoryName}`;

      const response = await getProjectSubcategoryList_API(query);

      if (response.status === HttpStatusCode.Ok) {
        setSubCategoryList(response.data);
        setProjectData({
          ...projectData,
          subCategoryName: response.data[0]?.name,
        });
      }

      if (response) {
        errorHandler(response);
      }
    } catch (error) {
      console.log(error, "subcategoryreponse errorr");
    }
  };

  const handleGetProjectDetail = async () => {
    try {
      setIsLoading(true);

      let query = `name=${activeProject?.name}`;

      const response = await getProjectDetail_API(query);

      if (response.status === HttpStatusCode.Ok) {
        setProjectData(response.data);
        setSelectedItemsList(response.data?.assignedUsers || []);
        setError(null);
      }

      if (response) {
        errorHandler(response, setIsLoading);
      }
    } catch (error) {
      console.log(error, "handleGetProjectDetail errorr");
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleUpdateAssignUser = async () => {
    try {
      setIsLoading(true);

      let body = {
        projectName: activeProject?.name,
        userEmails: selectedItemsList.map((item) => item.email),
      };

      const response = await assignProjectUsers_API(body);

      if (response.status === HttpStatusCode.Ok) {
        handleSnackbar(
          response?.message || "Project allocation successful",
          Severity.success
        );
        handleGetProjectDetail();
      }

      if (response) {
        errorHandler(response, setIsLoading);
      }
    } catch (error) {
      console.log(error, "handleGetProjectDetail errorr");
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleUpdateProject = async (projectData) => {
    try {
      setIsLoading(true);

      let body = {};

      if (projectData?.name !== activeProject?.name) {
        body.newName = projectData?.name;
      }
      if (projectData?.description !== activeProject?.description) {
        body.description = projectData?.description;
      }
      if (projectData?.categoryName !== activeProject?.categoryName) {
        body.categoryName = projectData?.categoryName;
      }
      if (projectData?.subCategoryName !== activeProject?.subCategoryName) {
        body.subCategoryName = projectData?.subCategoryName;
      }
      if (projectData?.status !== activeProject?.status) {
        body.status = projectData?.status;
      }
      if (projectData?.useCasePrefix !== activeProject?.useCasePrefix) {
        body.useCasePrefix = projectData?.useCasePrefix;
      }
      if (projectData?.userStoryPrefix !== activeProject?.userStoryPrefix) {
        body.userStoryPrefix = projectData?.userStoryPrefix;
      }
      if (projectData?.testCasePrefix !== activeProject?.testCasePrefix) {
        body.testCasePrefix = projectData?.testCasePrefix;
      }
      if (projectData?.releasePrefix !== activeProject?.releasePrefix) {
        body.releasePrefix = projectData?.releasePrefix;
      }
      if (projectData?.sprintPrefix !== activeProject?.sprintPrefix) {
        body.sprintPrefix = projectData?.sprintPrefix;
      }

      if (Object.keys(body)?.length > 0) {
        body.currentName = activeProject?.name;
        body.lastModifiedAt = activeProject?.lastModifiedAt
          ? activeProject?.lastModifiedAt
          : null;
      } else {
        setIsLoading(false);
        return;
      }

      const response = await updateProject_API(body);

      if (response) {
        if (response.status === HttpStatusCode.NotAcceptable) {
          let errorVar = {};

          Object.keys(response?.response.data.fields || {})?.forEach((key) => {
            let errorKeyName = key === "newName" ? "name" : key;
            errorVar[errorKeyName] = response?.response.data?.fields[key];
          });

          setError(errorVar === "{}" ? null : errorVar);
        } else {
          setError(null);
          errorHandler(response, setIsLoading);
        }
      }

      if (response.status === HttpStatusCode.Ok) {
        let list = [...projectList];
        let foundIndex = list?.findIndex(
          (item) => item?.name === body.currentName
        );
        if (foundIndex !== -1) {
          list[foundIndex] = {
            ...list[foundIndex],
            ...response.data,
            userStoryCount: list[foundIndex].userStoryCount,
            useCaseCount: list[foundIndex].useCaseCount,
            testCaseCount: list[foundIndex].testCaseCount,
          };
        }
        setProjectList(list);
        handleSnackbar("Project updated successfully", Severity.success);
        resetAi();
        handleContinue();
      }
    } catch (error) {
      console.log(error, "projectreponse errorr");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGetCategoryList();
  }, []);

  useEffect(() => {
    if (projectData?.categoryName) {
      handleGetsubCategoryList();
    }
  }, [projectData?.categoryName]);

  useEffect(() => {
    handleGetProjectDetail();
  }, [activeProject]);

  const DrawerList = (
    <Box
      sx={{ width: 420 }}
      role="presentation"
      className="flex flex-col h-full overflow-hidden"
    >
      <Box className="flex items-center justify-between px-4 py-2 border-b-2">
        <Typography fontSize={font.size.fs_4} color="textPrimary">
          {t("editProject")}
        </Typography>
        <IconButton onClick={() => toggleDrawer()}>
          <Close />
        </IconButton>
      </Box>

      <Box
        component={"form"}
        onSubmit={handleSubmit}
        className="flex-1 flex flex-col gap-0 px-0 py-0"
      >
        <Box className="flex-1 flex flex-col gap-0 px-0 py-0 overflow-y-auto">
          {/* tabs container */}
          <Box className="w-full flex items-center justify-center gap-4 px-0 py-0">
            <Tabs
              value={selectedTab}
              onChange={(event, newValue) => setSelectedTab(newValue)}
              variant="fullWidth"
              textColor="primary"
              indicatorColor="primary"
              sx={{ width: "100%" }}
            >
              {Object.keys(ProjectTabs).map((key) => {
                const capitalizedKey =
                  key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
                return <Tab key={key} label={capitalizedKey} />;
              })}
            </Tabs>
          </Box>

          {/* project details container */}
          {selectedTab === ProjectTabs.detail && (
            <Box className="flex-1 flex flex-col gap-4 px-4 py-4 overflow-y-auto">
              {/* ---------- Name ---------- */}
              <FormControl
                required
                fullWidth
                error={error?.name ? true : false}
              >
                <FormLabel htmlFor="name">{t("nameText")}</FormLabel>
                <TextField
                  id="name"
                  name="name"
                  placeholder="Enter project name"
                  sx={{ fontSize: font.size.fs_4 }}
                  value={projectData?.name || ""}
                  onChange={handleChange}
                  error={error?.name ? true : false}
                  helperText={error?.name && error?.name}
                />
              </FormControl>

              {/* ---------- Description + AI ---------- */}
              <FormControl
                required
                fullWidth
                error={error?.description ? true : false}
                sx={{ position: "relative" }}
              >
                <FormLabel htmlFor="description">
                  {t("descriptionText")}
                </FormLabel>
                <TextField
                  id="description"
                  name="description"
                  placeholder="Enter project description"
                  multiline
                  minRows={4}
                  sx={{
                    fontSize: font.size.fs_4,
                    "& .MuiInputBase-root": {
                      paddingBottom: "28px",
                    },
                  }}
                  value={projectData?.description || ""}
                  onChange={handleChange}
                  error={error?.description ? true : false}
                  helperText={error?.description && error?.description}
                  InputProps={{
                    endAdornment: projectData?.description?.trim() && (
                      <InputAdornment
                        position="end"
                        sx={{ alignSelf: "flex-start", mt: 1 }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          {/* AI icon — hidden while loading */}
                          <Collapse
                            in={!isImproving}
                            orientation="horizontal"
                            timeout={200}
                          >
                            <span>
                              <Tooltip
                                title={
                                  hasPendingSuggestion
                                    ? "Suggestion pending — accept or revert below"
                                    : improvementCount > 0
                                    ? "Improve text again with Floxpad AI"
                                    : "Improve text with Floxpad AI"
                                }
                                arrow
                                placement="top"
                              >
                                <span>
                                  <AICustomIcon
                                    size="xs"
                                    disabled={
                                      isLoading || hasPendingSuggestion
                                    }
                                    onClick={handleImproveText}
                                    showSparks={true}
                                    showBurst={true}
                                    showElectricRing={true}
                                    sx={{
                                      width: "28px",
                                      height: "28px",
                                      minWidth: "28px",
                                      minHeight: "28px",
                                      "& .ai-energy-icon": {
                                        width: "16px",
                                        height: "16px",
                                      },
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </span>
                          </Collapse>

                          {/* Copy */}
                          <Tooltip title="Copy text" arrow placement="top">
                            <span>
                              <IconButton
                                size="small"
                                onClick={handleCopyImproved}
                                disabled={isLoading}
                                sx={{ p: 0.5 }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* Reset */}
                          {improvementCount > 0 && originalText && (
                            <Tooltip
                              title="Reset to original"
                              arrow
                              placement="top"
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={handleResetToOriginal}
                                  disabled={isLoading}
                                  sx={{ p: 0.5 }}
                                >
                                  <Refresh fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />

                {/*  "Updating..." badge */}
                {isImproving && (
                  <Box
                    sx={{
                      position: "absolute",
                      right: 14,
                      bottom: error?.description ? 34 : 10,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 1,
                      py: 0.3,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.primary.main}40`,
                      boxShadow: `0 2px 8px ${theme.palette.primary.main}25`,
                      pointerEvents: "none",
                      zIndex: 2,
                      animation: "aiBadgeFadeIn 250ms ease-out",
                    }}
                  >
                    <CircularProgress
                      size={10}
                      thickness={5}
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        lineHeight: 1,
                        letterSpacing: 0.3,
                      }}
                    >
                      Updating...
                    </Typography>
                  </Box>
                )}

                {/* ---------- AI Suggestion Preview Panel ---------- */}
                {hasPendingSuggestion && (
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 1.5,
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.primary.main}40`,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}0d, ${theme.palette.secondary.main}0d)`,
                      position: "relative",
                      animation:
                        "aiPanelSlideIn 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {/* Header */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.75}
                      >
                        <AutoAwesome
                          fontSize="small"
                          sx={{ color: theme.palette.primary.main }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: theme.palette.primary.main,
                            letterSpacing: 0.3,
                          }}
                        >
                          Floxpad AI Suggestion
                        </Typography>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Tooltip title="Copy suggestion" arrow>
                          <IconButton
                            size="small"
                            onClick={handleCopySuggestion}
                            sx={{ p: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dismiss" arrow>
                          <IconButton
                            size="small"
                            onClick={handleRevertSuggestion}
                            sx={{ p: 0.5 }}
                          >
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 1.25 }} />

                    {/* Preview text */}
                    <Box
                      sx={{
                        maxHeight: 140,
                        overflowY: "auto",
                        pr: 0.5,
                        mb: 1.5,
                        "&::-webkit-scrollbar": { width: 4 },
                        "&::-webkit-scrollbar-thumb": {
                          borderRadius: 2,
                          backgroundColor: theme.palette.divider,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.5,
                        }}
                      >
                        {pendingSuggestion.text}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box
                      display="flex"
                      gap={1}
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        startIcon={<Undo fontSize="small" />}
                        onClick={handleRevertSuggestion}
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            borderColor: theme.palette.text.secondary,
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        Revert
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Check fontSize="small" />}
                        onClick={handleAcceptSuggestion}
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          color: theme.palette.secondary.contrastText,
                          boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                            boxShadow: `0 4px 12px ${theme.palette.primary.main}50`,
                          },
                        }}
                      >
                        Accept Changes
                      </Button>
                    </Box>
                  </Paper>
                )}

                {/* Error */}
                {aiError && (
                  <Alert
                    severity="error"
                    icon={<ErrorOutline />}
                    sx={{ mt: 1 }}
                    onClose={() => setAiError(null)}
                  >
                    {aiError}
                  </Alert>
                )}

                {/* Improvement counter */}
                {improvementCount > 0 && !hasPendingSuggestion && (
                  <FormHelperText
                    sx={{
                      color: theme.palette.success.main,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: "0.68rem",
                      fontWeight: 500,
                      mt: 0.5,
                      lineHeight: 1.3,
                      "& .MuiSvgIcon-root": { fontSize: "0.85rem" },
                    }}
                  >
                    <Check />
                    Floxpad AI Enhanced ({improvementCount} change
                    {improvementCount > 1 ? "s" : ""} applied)
                  </FormHelperText>
                )}
              </FormControl>

              {/* ---------- Category & SubCategory ---------- */}
              <Box className="flex flex-col gap-4">
                <Box className="flex items-center justify-between gap-4">
                  <FormControl
                    required
                    fullWidth
                    error={error?.categoryName ? true : false}
                  >
                    <FormLabel
                      htmlFor="category"
                      id="demo-simple-select-label"
                    >
                      {t("categoryText")}
                    </FormLabel>
                    <Select
                      id="category"
                      placeholder="Category"
                      variant="outlined"
                      name="categoryName"
                      required
                      disabled={!projectData?.categoryName}
                      value={projectData?.categoryName || ""}
                      displayEmpty
                      renderValue={() => projectData?.categoryName}
                      onChange={handleChange}
                      error={error?.categoryName ? true : false}
                    >
                      {categoryList?.map((item, index) => (
                        <MenuItem value={item} key={index}>
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>

                    {error?.categoryName && (
                      <FormHelperText>{error?.categoryName}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <Box className="flex items-center justify-between gap-2">
                  <FormControl
                    required
                    fullWidth
                    error={error?.subCategoryName ? true : false}
                  >
                    <FormLabel
                      htmlFor="subCategory"
                      id="demo-simple-select-label"
                    >
                      {t("subCategoryText")}
                    </FormLabel>
                    <Select
                      id="subCategory"
                      placeholder="Sub Category"
                      variant="outlined"
                      name="subCategoryName"
                      required
                      disabled={!projectData?.subCategoryName}
                      value={projectData?.subCategoryName || ""}
                      displayEmpty
                      renderValue={() => projectData?.subCategoryName}
                      onChange={handleChange}
                      error={error?.subCategoryName ? true : false}
                    >
                      {subCategoryList?.map((item, index) => (
                        <MenuItem value={item} key={index}>
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>

                    {error?.subCategoryName && (
                      <FormHelperText>
                        {error?.subCategoryName}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Box>
            </Box>
          )}

          {selectedTab === ProjectTabs.configuration && (
            <Box className="flex-1 flex flex-col gap-4 px-4 py-4 overflow-y-auto">
              <FormControl>
                <FormLabel htmlFor="userStoryPrefix">
                  User Story Prefix
                </FormLabel>
                <TextField
                  required
                  id="userStoryPrefix"
                  name="userStoryPrefix"
                  sx={{ fontSize: font.size.fs_4 }}
                  value={projectData?.userStoryPrefix || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="useCasePrefix">Use Case Prefix</FormLabel>
                <TextField
                  required
                  id="useCasePrefix"
                  name="useCasePrefix"
                  sx={{ fontSize: font.size.fs_4 }}
                  value={projectData?.useCasePrefix || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="testCasePrefix">Test Case Prefix</FormLabel>
                <TextField
                  required
                  id="testCasePrefix"
                  name="testCasePrefix"
                  sx={{ fontSize: font.size.fs_4 }}
                  value={projectData?.testCasePrefix || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="releasePrefix">Release Prefix</FormLabel>
                <TextField
                  required
                  id="releasePrefix"
                  name="releasePrefix"
                  sx={{ fontSize: font.size.fs_4 }}
                  value={projectData?.releasePrefix || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="sprintPrefix">Sprint Prefix</FormLabel>
                <TextField
                  required
                  id="sprintPrefix"
                  name="sprintPrefix"
                  sx={{ fontSize: font.size.fs_4 }}
                  value={projectData?.sprintPrefix || ""}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>
          )}

          {selectedTab === ProjectTabs.users && (
            <Box className="flex-1 flex flex-col gap-4 px-4 py-4 overflow-y-auto">
              <Box className="flex flex-col gap-2">
                <FormControl fullWidth>
                  <FormLabel htmlFor="users-autocomplete">
                    {"Assign User"}
                  </FormLabel>
                  <Autocomplete
                    multiple
                    id="users-autocomplete"
                    options={teamsList}
                    getOptionLabel={(option) =>
                      option.firstName +
                      " " +
                      option.lastName +
                      " - " +
                      option.email
                    }
                    value={selectedItemsList}
                    onChange={handleChangeAutocomplete}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    onClose={() => setInputValue("")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search using name / email"
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                    renderTags={() => null}
                    filterSelectedOptions
                    isOptionEqualToValue={(option, value) =>
                      option.firstName === value.firstName ||
                      option.lastName === value.lastName ||
                      option.email === value.email
                    }
                  />
                </FormControl>

                {selectedItemsList?.length === 0 ? (
                  <Box className="flex-1 flex flex-col items-center justify-center">
                    <Typography fontWeight="bold">
                      No assigned users
                    </Typography>
                    <Typography>Select users to assign</Typography>
                  </Box>
                ) : (
                  selectedItemsList.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,
                        p: 0,
                        border: `0px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography>
                        Assigned Users ({selectedItemsList.length || 0}):
                      </Typography>
                      <Box className="flex flex-col gap-2">
                        {selectedItemsList.map((project, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 2,
                              p: 2,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              backgroundColor:
                                theme.palette.highlight.main,
                            }}
                          >
                            <Avatar
                              variant="circular"
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor:
                                  theme.palette.secondary.main,
                                fontWeight: "bold",
                              }}
                            >
                              {project?.firstName?.[0] +
                                project?.lastName?.[0]}
                            </Avatar>

                            <Box className="flex-1 flex flex-col">
                              <Typography
                                fontWeight="bold"
                                title={project.name}
                              >
                                {project?.firstName +
                                  " " +
                                  project?.lastName}
                              </Typography>
                              <Typography
                                className="line-clamp-4"
                                title={project.description}
                              >
                                {project?.email}
                              </Typography>
                            </Box>

                            <Chip
                              key={index}
                              color="secondary"
                              label={project.role}
                            />
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteItems(project)}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  )
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Box className="flex items-center gap-4 px-4 py-4 border-t-2">
          <Button
            fullWidth
            variant="outlined"
            disabled={
              isLoading || !projectData?.name || !projectData?.description
            }
            onClick={() => toggleDrawer()}
          >
            {t("discardText")}
          </Button>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="secondary"
            sx={{ color: theme.palette.secondary.contrastText }}
            disabled={isLoading || hasPendingSuggestion}
            onClick={handleSubmit}
          >
            {t("updateText")}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Drawer
        open={open}
        onClose={handleClose}
        anchor="right"
        variant="temporary"
      >
        {DrawerList}
      </Drawer>
    </Box>
  );
}